import { pickT } from "@repo/shared";
import type { AssetClassForecastHorizon, AssetClassForecastSnapshot } from "@repo/shared/types";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { useGoalAssetClassForecastSnapshot } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { AssetClassForecastChart } from "../asset-class-forecast-chart";
import { T } from "./asset-class-forecast.i18n";

type AssetClassForecastProps = {
  goalId: string | null;
  config: {
    style?: string;
    title?: string;
    description?: string;
    showChart?: boolean;
    showTable?: boolean;
    /** QL-313 — which horizons the toggle offers; absent = all three. */
    enabledHorizons?: AssetClassForecastHorizon[];
    defaultHorizon?: AssetClassForecastHorizon;
  };
  language: string;
  chartColors?: string[] | null;
};

/**
 * QL-315 — the goal-scoped asset-class forecast: the mix across this goal's
 * portfolios over time.
 *
 * Purely informational. There is no validation-error state here and no
 * `hasValidationError` prop, unlike the analytics widgets around it: the
 * completion engine never reports this component, nothing gates on it, and a
 * missing snapshot is a plain "not yet" rather than a gap to be chased.
 */
export function AssetClassForecast({
  goalId,
  config,
  language,
  chartColors,
}: AssetClassForecastProps) {
  const t = pickT(T, language);
  const { data, isLoading } = useGoalAssetClassForecastSnapshot(goalId);

  if (isLoading) return null;

  if (!data) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notAvailable}
      </div>
    );
  }

  const snapshot =
    typeof data === "string"
      ? (() => {
          try {
            return JSON.parse(data as string);
          } catch {
            return null;
          }
        })()
      : (data as AssetClassForecastSnapshot & { error?: string });

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  if (snapshot.error) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 space-y-2">
        <div className="text-sm font-medium text-destructive">{t.unavailable}</div>
        <div className="text-xs text-muted-foreground">{String(snapshot.error)}</div>
        <div className="text-xs text-muted-foreground">{t.contactAdvisor}</div>
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <AssetClassForecastChart
        snapshot={snapshot}
        showChart={config.showChart !== false}
        showTable={config.showTable !== false}
        enabledHorizons={config.enabledHorizons}
        defaultHorizon={config.defaultHorizon ?? "10Y"}
        chartColors={chartColors}
        language={language}
      />
    </VisualizationContainer>
  );
}
